import ConfirmModal from 'components/modals/ConfirmModal';
import { REMOVE_FILE } from 'constants/constants';
import removeIcon from 'images/icons/svg/error.svg';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileModelPlus } from 'services/EpdClient';
import FileService from 'services/FileService';
import styled from 'styled-components';
import { ButtonSmall } from 'styles/Styles.styled';
import { toaster } from 'components/v2/toast';
import { isValidImageFile } from 'util/utils';
import { UploadButton } from './WizardStep2';
import TextWithAnimatedEllipsis from 'components/v2/file-upload/TextWithAnimatedEllipsis';

interface ImageUploadProps {
  name: string;
  images?: FileModelPlus[];
  onUpload(e: React.ChangeEvent<HTMLInputElement>): void;
  onReorder: (imgIds: string[]) => any;
  onChangeEpd: any;
  disabled: boolean;
  showUploadingIndicator: boolean;
}

const SUPPORTED_IMAGE_EXTENSIONS = ['.bmp', '.jpeg', '.jpg', '.png', '.tiff', '.tif'];
const INPUT_ACCEPT_ATTRIBUTE = SUPPORTED_IMAGE_EXTENSIONS.join(',');

export const ImagesUpload: React.FunctionComponent<ImageUploadProps> = ({
  name,
  images,
  onUpload,
  onReorder,
  onChangeEpd,
  disabled,
  showUploadingIndicator,
}) => {
  const { t } = useTranslation();
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);
  const [selectedImage, setSelectedImage] = useState<any>(null);
  const [deleteImage, setDeleteImage] = useState<any>(null);
  const [dragId, setDragId] = useState<any>(null);
  const [previewImages, setPreviewImages] = useState<PImg[] | undefined>(undefined);

  interface PImg {
    id: number;
    imgId?: string;
    imgName?: string | undefined;
    epdFileId?: string;
  }

  useEffect(() => {
    setPreviewImages(
      images?.map((img, index) => {
        return { id: index + 1, imgId: img.id, imgName: img.name, epdFileId: img.epdFileId } as PImg;
      })
    );
  }, [images]);

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) {
      return;
    }

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!isValidImageFile(file, SUPPORTED_IMAGE_EXTENSIONS)) {
        toaster({
          severity: 'error',
          summary: 'Invalid image extension',
          details: 'Available image formats: .png, .jpg, .tiff',
        });
        return;
      }
    }

    onUpload(e);
  };

  const closeDeleteModal = () => {
    setIsConfirmModalOpen(false);
  };

  const handleDeleteImage = (img: any) => {
    setIsConfirmModalOpen(true);
    setDeleteImage(img);
  };

  const handleOver = (ev: any) => {
    ev.preventDefault();
  };

  const handleDrag = (ev: any) => {
    setDragId(ev.currentTarget.id);
  };

  const handleDrop = (ev: any) => {
    ev.preventDefault();
    const dragImage = previewImages?.find((image) => image.id.toString() === dragId);
    const dropImage = previewImages?.find((image) => image.id.toString() === ev.currentTarget.id);
    if (dragImage && dropImage) {
      const arr = moveItem(dragImage?.id - 1, dropImage?.id - 1);
      if (arr && arr.length > 1) {
        onReorder(arr.map((i) => i.epdFileId || ''));
      }
      setPreviewImages(arr);
    }
  };

  const moveItem = (from: number, to: number) => {
    const f = previewImages?.splice(from, 1)[0];
    if (f) {
      previewImages?.splice(to, 0, f);
    }

    return previewImages?.map((img, index) => {
      return { id: index + 1, imgId: img.imgId, imgName: img.imgName, epdFileId: img.epdFileId } as PImg;
    });
  };

  const getImageBox = (img: any) => {
    return (
      <ImageBox
        key={img.id}
        id={img.id}
        draggable
        onDragOver={(e) => handleOver(e)}
        onDragStart={(e) => handleDrag(e)}
        onDrop={(e) => handleDrop(e)}
      >
        <ImageBoxImage>
          {img.imgId && (
            <img alt={`Custom ${name}`} src={FileService.getImageUrl(img.imgId)} onClick={() => setSelectedImage(img)} />
          )}
        </ImageBoxImage>
        <ImageNo>{img.id}</ImageNo>
        {!disabled && (
          <ImageRemove>
            <img src={removeIcon} style={{ height: '1rem' }} alt="Remove" onClick={() => handleDeleteImage(img)} />
          </ImageRemove>
        )}
        {img.id == 1 && <CoverImageTag>Cover image</CoverImageTag>}
      </ImageBox>
    );
  };

  return (
    <div>
      <UploadButton>
        <ButtonSmall disabled={disabled}>
          {t('upload')} {name}
        </ButtonSmall>
        <input
          type="file"
          accept={INPUT_ACCEPT_ATTRIBUTE}
          name={name}
          onChange={handleFileInputChange}
          disabled={disabled}
        />
      </UploadButton>
      <div style={{ display: 'flex', marginBottom: '1rem', flexWrap: 'wrap' }}>
        {previewImages && previewImages.map(getImageBox)}
      </div>

      {selectedImage && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0,0,0,0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}
        >
          <button
            onClick={() => setSelectedImage(null)}
            onKeyUp={(e) => {
              if (['Escape', 'Enter', ' '].includes(e.key)) {
                setSelectedImage(null);
              }
            }}
            onBlur={() => setSelectedImage(null)}
            style={{
              all: 'unset', // removes default button styling
              cursor: 'zoom-out',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              outline: 'none',
            }}
            autoFocus
          >
            <img
              src={FileService.getImageUrl(selectedImage.imgId)}
              alt="Full preview"
              style={{
                maxWidth: '90vw',
                maxHeight: '90vh',
                boxShadow: '0 0 10px #fff',
                borderRadius: '8px',
              }}
            />
          </button>
        </div>
      )}

      {isConfirmModalOpen && (
        <ConfirmModal
          description={t('confirmModal.deleteImage')}
          confirmButtonText={t('delete')}
          cancelButtonText={t('cancel')}
          onClickConfirm={() => {
            onChangeEpd(REMOVE_FILE, deleteImage?.imgId);
            closeDeleteModal();
          }}
          onClickCancel={() => closeDeleteModal()}
        />
      )}
      {showUploadingIndicator && <UploadingText>{t('epdWizard.wizardStep2.upload.uploadingFile')}</UploadingText>}
    </div>
  );
};

const CoverImageTag = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  height: 27px;
  display: flex;
  width: 100%;
  align-items: center;
  padding: 0 1rem;
  background-color: rgba(0, 0, 0, 0.6);
  color: ${(props) => props.theme.colors.primaryWhite};
  font-size: 16px;
  font-weight: 500;
  font-style: italic;
  line-height: 100%;
  letter-spacing: 0%;
`;

const ImageBox = styled.div`
  position: relative;
  flex: 0 0 46%;
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
  margin: 1rem 1rem 0 0;
  background-color: ${(props) => props.theme.colors.lightGreen};
`;

const ImageBoxImage = styled.div`
  flex: 0 0 auto;
  align-self: center;
  width: 200px;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    max-width: 150px;
    max-height: 150px;
  }
`;

const ImageRemove = styled.div`
  margin: 0.5rem;
  position: absolute;
  right: 0;
  cursor: pointer;
`;

const ImageNo = styled.div`
  margin: 0.5rem;
  position: absolute;
  left: 0;
`;

const UploadingText = styled(TextWithAnimatedEllipsis)`
  color: inherit;
  margin-top: 16px;
`;
